/**
 * Tokens de design — **source unique de vérité** (D§2).
 *
 * Aucune valeur de couleur, de rayon, d'ombre, de durée ou de taille de texte ne doit
 * exister ailleurs dans le code. Les feuilles de style consomment des variables CSS que
 * ce module émet : TypeScript et CSS ne peuvent donc pas diverger, puisqu'il n'y a qu'une
 * seule définition.
 *
 * Les garde-fous du §2 sont vérifiés par les tests, pas seulement écrits ici :
 * trois rayons, trois élévations, sept niveaux typographiques, et **une couleur par
 * domaine, jamais réutilisée**.
 */

/* ── Couleurs de surface ────────────────────────────────────────────────────
   Nuit désaturée, jamais de noir pur. Le crème est du carton chaud, pas du papier. */

export const SURFACE = {
  night950: '#0e1626',
  night900: '#131e33',
  night850: '#18263e',
  night800: '#1e2f4b',
  night700: '#2a3e60',
  cream50: '#fdf7ea',
  cream100: '#f6e9d0',
  cream200: '#e6d3ae',
  cream300: '#cbb086',
} as const;

export const TEXT = {
  onNight: '#eaf0fa',
  onNightDim: '#93a3bf',
  onNightFaint: '#64748f',
  onCream: '#2b2416',
  onCreamSoft: '#7a684a',
} as const;

/* ── Accents fonctionnels ───────────────────────────────────────────────────
   Une couleur = un sens (D§2). Ajouter un domaine se discute ; réutiliser une
   teinte pour deux sens est un bug de design que le test `tokens` attrape. */

export const DOMAIN = {
  /** Pièces et production. */
  coins: { base: '#f7b23b', deep: '#b9761a' },
  /** Gemmes et jalons. */
  gems: { base: '#3fd3cb', deep: '#1a8d88' },
  /** Alertes, refus, urgence. */
  alert: { base: '#ff6f5e', deep: '#cf3f2a' },
  /** Partage, viral, visiteurs. */
  social: { base: '#5ed88c', deep: '#2b9c57' },
  /** Prestige et héritage. */
  prestige: { base: '#a276f0', deep: '#6a3fbb' },
} as const;

export type DomainName = keyof typeof DOMAIN;

/** Divisions de ligue — une échelle, pas un domaine de plus. */
export const LEAGUE = {
  wood: '#9a6b45',
  stone: '#8f9aa8',
  bronze: '#c9803f',
  silver: '#c3cedc',
  gold: '#efc75e',
  diamond: '#7fe3ff',
  legend: '#ff9de0',
} as const;

/* ── Rayons : trois valeurs, pas quatre (D§2) ──────────────────────────── */

export const RADIUS = {
  small: 10,
  medium: 16,
  panel: 26,
} as const;

/* ── Élévations : posé, soulevé, flottant (D§2) ─────────────────────────

   ⚠️ UNE ÉLÉVATION EST UNE MATIÈRE, PAS UNE OMBRE.

   Les trois niveaux ne portaient qu'une `box-shadow`, et `.v-panel` appliquait le
   MÊME fond et le MÊME liseré aux trois. Résultat mesuré sur la planche
   `design/fondations-elevations.html` : les trois panneaux sont indiscernables sans
   leur étiquette. Le bandeau haut n'avait donc aucun point focal — trois dalles de
   même poids visuel, dont celle qui porte le solde du joueur.

   La logique est physique et il faut s'y tenir : plus un panneau est haut, plus il est
   PROCHE DE LA LUMIÈRE. Il s'éclaircit, son arête supérieure se précise, et son ombre
   s'éloigne. Chaque niveau monte donc d'un cran dans la gamme de nuit, et l'opacité du
   liseré suit (12 % → 20 % → 30 %).

   Le liseré reste présent aux trois niveaux : c'est lui qui donne l'épaisseur, l'ombre
   seule ne suffit pas. */

/** Liseré clair sur l'arête supérieure — l'épaisseur physique du panneau. */
const topEdge = (opacity: number): string =>
  `0 1px 0 0 rgb(255 255 255 / ${String(opacity)}%) inset`;

export interface ElevationLevel {
  /** Ombre portée, plus le liseré d'arête. */
  readonly shadow: string;
  /** Fond du panneau. C'est LUI qui sépare les trois niveaux au premier coup d'œil. */
  readonly fill: string;
  /** Bordure extérieure, d'autant plus nette que le panneau est haut. */
  readonly edge: string;
}

export const ELEVATION: Readonly<Record<'rest' | 'raised' | 'floating', ElevationLevel>> = {
  /** Posé sur la ville : HUD, pastilles. Le plus sombre — il appartient au décor. */
  rest: {
    shadow: `${topEdge(12)}, 0 2px 10px rgb(4 9 18 / 45%)`,
    fill: `linear-gradient(180deg, ${SURFACE.night850}, ${SURFACE.night950})`,
    edge: 'rgb(255 255 255 / 8%)',
  },
  /** Soulevé : tiroirs, cartes, panneaux latéraux. */
  raised: {
    shadow: `${topEdge(20)}, 0 14px 30px -8px rgb(4 9 18 / 62%), 0 2px 6px rgb(4 9 18 / 38%)`,
    fill: `linear-gradient(180deg, ${SURFACE.night800}, ${SURFACE.night900})`,
    edge: 'rgb(255 255 255 / 13%)',
  },
  /** Flottant : modales, toasts. Le plus clair — il se détache de tout le reste. */
  floating: {
    shadow: `${topEdge(30)}, 0 30px 70px -14px rgb(2 6 14 / 80%), 0 6px 16px rgb(2 6 14 / 48%)`,
    fill: `linear-gradient(180deg, ${SURFACE.night700}, ${SURFACE.night850})`,
    edge: 'rgb(255 255 255 / 20%)',
  },
};

/* ── Mouvement (D§2) ────────────────────────────────────────────────────── */

export const DURATION = {
  /** Micro-interactions : enfoncement, survol, bascule. */
  micro: 150,
  /** Panneaux : tiroirs, apparitions, transitions d'écran. */
  panel: 250,
  /** Célébrations : jalons, prestige, extension de terrain. */
  celebration: 400,
} as const;

export const EASING = {
  /** Par défaut. */
  out: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  /** Tout ce qui apparaît. */
  back: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  /** Mouvements de caméra. */
  inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
} as const;

/* ── Typographie : sept niveaux, pas huit (D§2) ─────────────────────────
   `display` et `body` sont les deux rôles ; les chiffres sont toujours tabulaires,
   sinon un compteur qui tweene tremble à chaque frame.

   Nunito est hébergée en propre (`public/fonts`, licence OFL, voir CREDITS.md).
   Elle vient APRÈS `ui-rounded` : sur iOS et macOS le système fournit SF Pro
   Rounded, plus finement optimisée pour ces écrans. Ailleurs — Android, Windows —
   Nunito prend le relais, ce qui évite un jeu soigné sur iPhone et fade partout
   ailleurs. Mesure faite : les chiffres de Nunito ont tous la même chasse, sans
   dépendre de la fonctionnalité OpenType `tnum` (Fredoka et Quicksand échouaient
   sur ce point). */

export const FONT = {
  display: `ui-rounded, "SF Pro Rounded", "Nunito", "Segoe UI Variable Display", system-ui, sans-serif`,
  body: `system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif`,
} as const;

export interface TypeLevel {
  readonly name: string;
  readonly size: number;
  readonly weight: number;
  readonly lineHeight: number;
}

export const TYPE_SCALE: readonly TypeLevel[] = [
  { name: 'display', size: 40, weight: 800, lineHeight: 1.02 },
  { name: 'title1', size: 27, weight: 700, lineHeight: 1.12 },
  { name: 'title2', size: 21, weight: 700, lineHeight: 1.2 },
  { name: 'title3', size: 17, weight: 700, lineHeight: 1.3 },
  { name: 'body', size: 15, weight: 400, lineHeight: 1.55 },
  { name: 'small', size: 13, weight: 400, lineHeight: 1.45 },
  { name: 'label', size: 11, weight: 700, lineHeight: 1.2 },
] as const;

/* ── Ergonomie tactile (D§3) ───────────────────────────────────────────── */

export const TOUCH = {
  /** Cible tactile minimale. */
  target: 48,
  /** Écart minimal entre deux cibles. */
  gap: 8,
} as const;

/* ── Émission vers CSS ──────────────────────────────────────────────────── */

function px(value: number): string {
  return `${value}px`;
}

/**
 * Construit la liste des variables CSS à partir des tokens.
 * Fonction pure : c'est elle que les tests inspectent.
 */
export function tokenEntries(): readonly (readonly [string, string])[] {
  const entries: [string, string][] = [];

  for (const [name, value] of Object.entries(SURFACE)) {
    entries.push([`--surface-${kebab(name)}`, value]);
  }
  for (const [name, value] of Object.entries(TEXT)) {
    entries.push([`--text-${kebab(name)}`, value]);
  }
  for (const [domain, tones] of Object.entries(DOMAIN)) {
    entries.push([`--${domain}`, tones.base], [`--${domain}-deep`, tones.deep]);
  }
  for (const [name, value] of Object.entries(LEAGUE)) {
    entries.push([`--league-${name}`, value]);
  }
  for (const [name, value] of Object.entries(RADIUS)) {
    entries.push([`--radius-${name}`, px(value)]);
  }
  // `--elevation-*` reste l'OMBRE seule : une douzaine de composants s'en servent pour
  // se poser sans être des panneaux. Le fond et le liseré s'ajoutent à côté.
  for (const [name, level] of Object.entries(ELEVATION)) {
    entries.push(
      [`--elevation-${name}`, level.shadow],
      [`--panel-${name}-fill`, level.fill],
      [`--panel-${name}-edge`, level.edge],
    );
  }
  for (const [name, value] of Object.entries(DURATION)) {
    entries.push([`--duration-${name}`, `${value}ms`]);
  }
  for (const [name, value] of Object.entries(EASING)) {
    entries.push([`--ease-${kebab(name)}`, value]);
  }
  for (const level of TYPE_SCALE) {
    entries.push(
      [`--type-${level.name}-size`, px(level.size)],
      [`--type-${level.name}-weight`, String(level.weight)],
      [`--type-${level.name}-line`, String(level.lineHeight)],
    );
  }
  entries.push(
    ['--font-display', FONT.display],
    ['--font-body', FONT.body],
    ['--touch-target', px(TOUCH.target)],
    ['--touch-gap', px(TOUCH.gap)],
  );

  return entries;
}

/**
 * `onNight` → `on-night`, mais `night950` reste `night950`.
 *
 * Ne couper QUE sur les changements de casse. Couper aussi devant les chiffres
 * produisait `--surface-night-950` alors que toutes les feuilles de style écrivent
 * `--surface-night950` : les variables n'existaient pas, les panneaux perdaient leur
 * fond et les cartes leur carton. Le défaut ne se voyait pas dans les tests, qui
 * vérifiaient la forme des noms et non leur valeur exacte.
 */
function kebab(name: string): string {
  return name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/** Bloc CSS `:root { … }` prêt à être injecté. */
export function tokensToCss(): string {
  const body = tokenEntries()
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
  return `:root {\n${body}\n}`;
}

/**
 * Pose les tokens sur le document. À appeler **avant tout rendu**, une seule fois.
 * Écrire des propriétés personnalisées plutôt qu'une feuille de style évite un
 * flash de couleurs par défaut au démarrage.
 */
export function applyTokens(target: HTMLElement = document.documentElement): void {
  for (const [name, value] of tokenEntries()) {
    target.style.setProperty(name, value);
  }
}
